import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ConfigureTab from "@/components/assistentes/tabs/ConfigureTab";
import PromptTab from "@/components/assistentes/tabs/PromptTab";
import AssistantSimulationChat from "@/components/assistentes/AssistantSimulationChat";
import { Button } from "@/components/ui/button";
import { useAssistants } from "@/hooks/useAssistants";

const AssistenteNovo: React.FC = () => {
  const navigate = useNavigate();
  const { createAssistant } = useAssistants();

  const [activeTab, setActiveTab] = useState("configure");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    model: 'gpt-5',
    response_time_value: 10,
    response_time_unit: 'seconds',
    max_messages: 1,
    prompt_mode: 'guided-mode',
    default_prompt: '',
    voice_tone: '',
    main_objective: '',
    language_rules: '',
    good_examples: '',
    restrictions: '',
    additional_info: '',
    is_active: true
  });

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Nome do assistente é obrigatório' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const result = await createAssistant(formData);

      if (result.success && result.id) {
        setMessage({ type: 'success', text: 'Assistente criado! Redirecionando para configuracao...' });
        setTimeout(() => {
          navigate(`/assistentes/editar/${result.id}`);
        }, 1000);
      } else {
        setMessage({ type: 'error', text: 'Falha ao criar assistente' });
      }
    } catch (error) {
      console.error('Erro ao criar assistente:', error);
      setMessage({ type: 'error', text: 'Erro ao criar assistente' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-[calc(100dvh-110px)] flex flex-col">
      <div className="mb-4 flex items-center gap-4">
        <button
          onClick={() => navigate('/assistentes')}
          className="flex items-center gap-2 text-neutral-50 hover:text-neutral-300 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </button>
        <h2 className="text-[26px] sm:text-[30px] font-semibold text-neutral-50">
          Novo Assistente
        </h2>
      </div>

      {message && (
        <div className={`p-3 rounded-md mb-4 ${
          message.type === 'success'
            ? 'bg-green-500/20 border border-green-500/30 text-green-300'
            : 'bg-red-500/20 border border-red-500/30 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 flex flex-col">
          <div className="flex-1 min-h-0 border-[1px] border-[#3F3F464D] rounded-[12px] bg-[#18181B] p-4 overflow-hidden flex flex-col">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <TabsList className="mb-4">
                <TabsTrigger value="configure">Configure</TabsTrigger>
                <TabsTrigger value="prompt">Prompt</TabsTrigger>
                <TabsTrigger
                  value="knowledge-base"
                  disabled
                  className="opacity-50 cursor-not-allowed flex items-center gap-1.5"
                  title="Disponivel apos criar o assistente"
                >
                  <Lock size={12} />
                  Base de conhecimento
                </TabsTrigger>
                <TabsTrigger
                  value="integrations"
                  disabled
                  className="opacity-50 cursor-not-allowed flex items-center gap-1.5"
                  title="Disponivel apos criar o assistente"
                >
                  <Lock size={12} />
                  Integrações
                </TabsTrigger>
                <TabsTrigger
                  value="webhooks"
                  disabled
                  className="opacity-50 cursor-not-allowed flex items-center gap-1.5"
                  title="Disponivel apos criar o assistente"
                >
                  <Lock size={12} />
                  Webhooks
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto">
                <TabsContent value="configure" className="mt-0">
                  <ConfigureTab formData={formData} onFormChange={handleFormChange} />
                </TabsContent>
                <TabsContent value="prompt" className="mt-0">
                  <PromptTab formData={formData} onFormChange={handleFormChange} />
                </TabsContent>
              </div>
            </Tabs>

            {(activeTab === "configure" || activeTab === "prompt") && (
              <div className="mt-4 pt-4 border-t border-[#3F3F464D]">
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  className="w-[120px]"
                >
                  {loading ? 'Criando...' : 'Criar'}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4 h-full min-h-[500px] lg:min-h-0">
          <AssistantSimulationChat assistant={formData as any} />
        </div>
      </div>
    </div>
  );
};

export default AssistenteNovo;
